import React, { createContext, useContext, useState } from "react";
import { toLower, isArray, includes } from "lodash"
import config from "../config";
import useAxios from "../adapter/useAxios";
import { ASSET_TYPE, SERIES_TYPES } from "../constants/ContentFinder";
import { UserContext } from "./UserContext";
import { UserRoles } from "../constants/UserRoles";
import ErrorRedirect from "../components/error-redirect/ErrorRedirect";
import LoadingSpinner from "../components/common/LoadingSpinner";

const initialState = {
  recordType: "",
  idType: "",
  searchValue: "",
  results: [],
  loading: false,
  pageError: false,
  hasSearched: false,
  _setRecordType: (recordType) => {},
  _setIdType: (idType) => {},
  _setSearchValue: (searchValue) => {},
  _setResults: (results) => {},
  _setLoading: (loading) => {},
  _setPageError: (pageError) => {},
  _setHasSearched: (hasSearched) => {},
  searchContent: () => {},
  titleDetails: [],
  titleDetailsLoading: false,
  titleDetailsError: false,
  fetchTitleDetails: (titleId, titleType?) => {},
  _clearTitleDetails: () => {},
};

export const ContentFinderContext =
  createContext(initialState);

export const ContentFinderProvider = ({
  children,
}) => {
  const { userRole, isUserLoading } = useContext(UserContext); 
  const [recordType, setRecordType] = useState("");
  const [idType, setIdType] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageError, setPageError] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [titleDetails, setTitleDetails] = useState<any>([]);
  const [titleDetailsLoading, setTitleDetailsLoading] = useState(false);
  const [titleDetailsError, setTitleDetailsError] = useState(false);
  const { post, get } = useAxios();

  const _setRecordType = (value) => {
    setRecordType(value);
  };

  const _setIdType = (value) => {
    setIdType(value);
  };

  const _setResults = (value) => {
    setResults(value);
  };

  const _setLoading = (value) => {
    setLoading(value);
  };

  const _setPageError = (value) => {
    setPageError(value);
  };

  const _setSearchValue = (value) => {
    setSearchValue(value);
  };

  const _setHasSearched = (value) => {
    setHasSearched(value);
  };

  const searchContent = () => {
    const { searchLegacyGmsc } = config.apiUrls;
    const payload = {
      type: recordType === ASSET_TYPE.ID ? idType : recordType,
      value: searchValue.trim(),
    };
    setLoading(true);
    setHasSearched(true);
    post({  
      url: searchLegacyGmsc,
      data: payload,
    })
    .then((response) => {
      setResults(response);
    })
    .catch((error) => {
      console.log(error);
      setPageError(true);
      setResults([]);
    })
    .finally(() => {
      setLoading(false);
    });
  };

  const _clearTitleDetails = () => {
    setTitleDetails([]);
    setTitleDetailsError(false);
    setTitleDetailsLoading(false);
  };

  const fetchTitleDetails = (titleId, titleType) => {
    const { fetchTitleDetails: titleDetailsUrl } = config.apiUrls;
    if (!titleId) return;
    const isSeries = includes(SERIES_TYPES, toLower(titleType ?? ""));
    const type = isSeries ? "series" : "feature";
    setTitleDetailsLoading(true);
    setTitleDetailsError(false);
    get({
      url: `${titleDetailsUrl}${type}/${titleId}`,
    })
      .then((response) => {
        const titleDetails = isArray(response) ? response : [response];
        setTitleDetails(titleDetails);
      })
      .catch((error) => {
        console.log(error);
        setTitleDetailsError(true);
        setTitleDetails([]);
      })
      .finally(() => {
        setTitleDetailsLoading(false);
      });
  };

  return (
    <ContentFinderContext.Provider
      value={{
        recordType,
        idType,
        results,
        loading,
        pageError,
        searchValue,
        _setRecordType,
        _setIdType,
        _setResults,
        _setLoading,
        _setPageError,
        searchContent,
        _setSearchValue,
        hasSearched,
        _setHasSearched,
        titleDetails,
        titleDetailsLoading,
        titleDetailsError,
        fetchTitleDetails,
        _clearTitleDetails,
      }}
    >
       {
        toLower(userRole) === UserRoles.GLOBALADMIN ||
        toLower(userRole) === UserRoles.LEGACY_GMSC ? (
        children
      ) : (
        <ErrorRedirect
          error={{
            errorCode: "access_denied",
            type: "restricted",
            message: "User is not authorized to access this page.",
            redirect: toLower(userRole) === UserRoles.CR_API ? true : false,
          }}
        />
      )}
      <LoadingSpinner show={isUserLoading} />
    </ContentFinderContext.Provider>
  );
};